import React, { ReactNode } from 'react';

interface ActionButtonProps {
  children: ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  color?:
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'success'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  testIdIdentifier?: string;
}

const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(({
  children,
  onClick,
  color = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  className = '',
  testIdIdentifier,
}, ref) => {
  // Base classes for all buttons
  const baseClasses =
    'inline-flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

  // Size variations
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  // Color variations
  const colorClasses = {
    // Filled variants
    primary: 'bg-blue-600 border-2 border-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
    secondary: 'bg-indigo-600 border-2 border-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500',
    danger: 'bg-red-600 border-2 border-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    warning: 'bg-amber-500 border-2 border-amber-500 hover:bg-amber-600 text-white focus:ring-amber-500',
    success: 'bg-green-600 border-2 border-green-600 hover:bg-green-700 text-white focus:ring-green-500',

    // Outline variants with enhanced clickable appearance
    'outline-primary':
      'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md focus:ring-blue-500 shadow-sm',
    'outline-secondary':
      'bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 hover:shadow-md focus:ring-indigo-500 shadow-sm',
    'outline-danger':
      'bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 hover:shadow-md focus:ring-red-500 shadow-sm',
    'outline-warning':
      'bg-white border-2 border-amber-500 text-amber-500 hover:bg-amber-50 hover:text-amber-600 hover:shadow-md focus:ring-amber-500 shadow-sm',
    'outline-success':
      'bg-white border-2 border-green-600 text-green-600 hover:bg-green-50 hover:text-green-700 hover:shadow-md focus:ring-green-500 shadow-sm',
  };

  // Disabled state
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer';

  // Width
  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-cy={testIdIdentifier}
      className={`${baseClasses} ${sizeClasses[size]} ${colorClasses[color]} ${widthClasses} ${disabledClasses} ${className}`}
      ref={ref}
    >
      {children}
    </button>
  );
});

export default ActionButton;
